import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';

// 'nav-home' deliberately omitted — Home is hidden from the sidebar (Mo,
// 2026-05-26). The view itself still renders as the default landing view;
// 'Go to Work' (G W) reaches the same surface. Inbox + Reporting were never
// in this palette. Reversible: re-add `{ id: 'nav-home', ... }`.
const NAV_ITEMS = [
  { id: 'nav-work', label: 'Go to Work', hint: 'G W', view: 'myday', icon: '📋' },
  { id: 'nav-stock', label: 'Go to Stock', hint: 'G S', view: 'forecaster', icon: '📦' },
  { id: 'nav-finance', label: 'Go to Finance', hint: 'G F', view: 'finance', icon: '💰' },
  { id: 'nav-people', label: 'Go to People', hint: 'G P', view: 'contacts', icon: '👥' },
  { id: 'nav-files', label: 'Go to Files', hint: 'G K', view: 'files', icon: '📁' },
  { id: 'nav-archive', label: 'Go to Archive', hint: 'G A', view: 'archive', icon: '🗄' },
];

function fuzzyMatch(text, query) {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  if (lower.includes(q)) return true;
  let qi = 0;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

export default function CommandPalette({ open, onClose, onNavigate, session, staff = [], tasks = [] }) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { if (open) { setQuery(''); setActiveIdx(0); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);

  const results = useMemo(() => {
    const items = [];
    const q = query.trim();

    if (!q) {
      // Default actions
      items.push({ id: 'action-new-task', label: '+ New task', type: 'action', action: 'new-task', icon: '➕' });
      items.push({ id: 'action-shutdown', label: 'Shutdown for today', type: 'action', action: 'shutdown', icon: '🌙' });
      NAV_ITEMS.forEach(n => items.push({ ...n, type: 'nav' }));
      // Person switcher for admins. After mig 139 only admins see the
      // `code` column (via admin_staff_directory()), so filter on its
      // presence rather than blindly emitting broken `switch-undefined`
      // entries for non-admin builds.
      if (session?.role === 'admin') {
        staff.forEach(s => { if (s.code) items.push({ id: `switch-${s.code}`, label: `Switch to ${s.name}`, type: 'action', action: `switch-${s.code}`, icon: '👤' }); });
      }
      return items;
    }

    // Fuzzy search
    // Tasks
    tasks.filter(t => t.status !== 'done' && t.status !== 'archived').forEach(t => {
      if (fuzzyMatch(t.title, q)) {
        items.push({ id: `task-${t.id}`, label: t.title, type: 'task', taskId: t.id, subtitle: `Task · ${t.due_date ? (t.due_date === new Date().toISOString().slice(0, 10) ? 'Today' : t.due_date) : 'No date'}`, icon: '📋' });
      }
    });

    // Nav
    NAV_ITEMS.forEach(n => { if (fuzzyMatch(n.label, q)) items.push({ ...n, type: 'nav' }); });

    // Actions
    if (fuzzyMatch('new task', q)) items.push({ id: 'action-new-task', label: '+ New task', type: 'action', action: 'new-task', icon: '➕' });
    if (fuzzyMatch('shutdown', q)) items.push({ id: 'action-shutdown', label: 'Shutdown for today', type: 'action', action: 'shutdown', icon: '🌙' });

    // People — `s.code` may be missing on non-admin builds; fall back
    // to the staff name as the React key (names are unique within the
    // active staff set).
    staff.forEach(s => { if (fuzzyMatch(s.name, q)) items.push({ id: `person-${s.code || s.name}`, label: s.name, type: 'person', subtitle: 'Person', icon: '👤' }); });

    return items.slice(0, 8);
  }, [query, tasks, staff, session]);

  function select(item) {
    if (item.type === 'nav') { onNavigate(item.view); onClose(); }
    else if (item.type === 'action') { onNavigate(null, item.action); onClose(); }
    else if (item.type === 'task') { onNavigate(null, 'open-task', item.taskId); onClose(); }
    else onClose();
  }

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[activeIdx]) { e.preventDefault(); select(results[activeIdx]); }
    else if (e.key === 'Escape') { onClose(); }
  }

  // Scroll active into view
  useEffect(() => {
    if (listRef.current?.children[activeIdx]) {
      listRef.current.children[activeIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  // Reset active index when results change
  useEffect(() => { setActiveIdx(0); }, [query]);

  if (!open) return null;

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', zIndex: 9990 }} />
      <motion.div
        initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}
        style={{ position: 'fixed', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 640, maxWidth: '90vw', background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', zIndex: 9991, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subdued)' }}>
          <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown}
            placeholder="Type a command or search..."
            style={{ width: '100%', border: 'none', outline: 'none', fontSize: 16, fontFamily: 'inherit', color: '#0a0a0a', background: 'transparent' }} />
        </div>
        <div ref={listRef} style={{ maxHeight: 360, overflowY: 'auto', padding: '8px 0' }}>
          {results.map((item, i) => (
            <button key={item.id} onClick={() => select(item)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', border: 'none', background: i === activeIdx ? '#f4f4f5' : 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
              onMouseEnter={() => setActiveIdx(i)}>
              <span style={{ fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#0a0a0a' }}>{item.label}</div>
                {item.subtitle && <div style={{ fontSize: 12, color: '#a1a1aa' }}>{item.subtitle}</div>}
              </div>
              {item.hint && <span style={{ fontSize: 11, color: '#a1a1aa', background: '#f4f4f5', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace' }}>{item.hint}</span>}
            </button>
          ))}
          {results.length === 0 && query && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#a1a1aa', fontSize: 13 }}>No results for "{query}"</div>
          )}
        </div>
      </motion.div>
    </>,
    document.body
  );
}
